import { PAPEIS, ROTULO_PAPEL, podeAtribuirPapel, type Papel } from '@baiocchi/shared'
import {
  Alerta,
  AreaTexto,
  Botao,
  CabecalhoCartao,
  Caixa,
  Campo,
  Cartao,
  ContadorTexto,
  CorpoCartao,
  Entrada,
  EstadoCarregando,
  Etiqueta,
  ListaDefinicao,
  Modal,
  Selecao,
  SelecaoMultipla,
  useAvisos,
} from '@baiocchi/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { CODIGOS_ERRO } from '@baiocchi/shared'
import { ErroApi } from '../../api/erros'
import { equipesApi, usuariosApi } from '../../api/organizacao'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeTransferir, IconeVoltar } from '../../componentes/icones'
import { opcoesDePessoas, useEquipes, useUnidades, opcoesDeReferencia } from '../../ganchos/usar-catalogos'
import { CabecalhoPagina } from '../../layout/layout-app'
import { formatarDataHora } from '../../utilitarios/formatar'

/**
 * O contrato manda `{ ddi, numero }`, como o resto do sistema
 * (`clientes.contato_principal_*`); o formulário tem um campo só, e o DDI padrão é 55.
 * O E.164 é derivado no **servidor** por `normalizarTelefone` — a tela manda o que foi
 * digitado, e o `422` volta apontando `celular.numero` se não der.
 */
function montarCelular(bruto: string): { ddi: string; numero: string } | null {
  const numero = bruto.trim()
  return numero === '' ? null : { ddi: '55', numero }
}

/**
 * Igualdade como conjunto, para não disparar `PUT` de vínculo que não mudou.
 *
 * Não é otimização: cada `PUT /equipes` e `PUT /unidades` revoga todas as sessões do
 * usuário (RN-73) e escreve na auditoria (RN-74). Reenviar o mesmo conjunto porque a
 * ordem dos ids voltou diferente do servidor derrubaria a sessão de quem não teve
 * alteração nenhuma.
 */
function mesmoConjunto(a: ReadonlyArray<number>, b: ReadonlyArray<number>): boolean {
  if (a.length !== b.length) return false
  const conjunto = new Set(a)
  return b.every((valor) => conjunto.has(valor))
}

interface EstadoUsuario {
  nome: string
  email: string
  papel: Papel
  /**
   * Lotação — `usuarios.unidade_id`. **Não** é o escopo de leitura (decisão B); é a
   * unidade à qual a pessoa pertence, e é o único vínculo de unidade que o ATENDENTE
   * tem.
   */
  unidadeId: string
  /**
   * `usuarios_unidades` — escopo de leitura do GESTOR. Múltiplo desde sempre no
   * banco; esta tela é que só sabia gravar um.
   */
  unidadeEscopoIds: string[]
  /** `usuarios_equipes` — escopo de leitura do SUPERVISOR. */
  equipeIds: string[]
  celular: string
  receberLeads: boolean
  ativo: boolean
  enviarConvite: boolean
}

/**
 * TELA-10 / TELA-11 — Editar e criar usuário. Um formulário só.
 *
 * **Não há campo de senha.** Alteração deliberada em relação ao legado, exigida pela
 * spec: o que se envia é um convite com link de definição de senha (token de uso
 * único). Senha em texto claro por e-mail é o defeito que a migração precisa não
 * copiar, e "definir senha de outra pessoa" é privilégio exclusivo do Administrador.
 *
 * **Desativar exige tratativa dos leads em aberto** (RN-07A). A API responde `409`
 * com a quantidade quando falta o destino; a tela trata esse `409` como **passo do
 * fluxo**, abrindo a escolha do destino, e não como erro.
 */
export function PaginaFormularioUsuario() {
  const { uuid } = useParams()
  const ehNovo = uuid === undefined
  const navegar = useNavigate()
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()
  const { usuario: eu, pode } = useSessao()
  /** `USUARIO_VINCULAR_UNIDADES` — exclusivo do Administrador (`04-roles §3.5`). */
  const podeVincularUnidades = pode('USUARIO_VINCULAR_UNIDADES')

  const [estado, setEstado] = useState<EstadoUsuario>({
    nome: '',
    email: '',
    papel: 'ATENDENTE',
    unidadeId: '',
    unidadeEscopoIds: [],
    equipeIds: [],
    celular: '',
    receberLeads: true,
    ativo: true,
    enviarConvite: true,
  })
  const [erro, setErro] = useState<ErroApi | null>(null)
  const [modalDesativar, setModalDesativar] = useState(false)
  const [modalTransferir, setModalTransferir] = useState<'responsavel' | 'equipe' | null>(null)

  const unidades = useUnidades()
  /**
   * Sem filtro de unidade na consulta: o recorte agora é por **várias** unidades, e
   * `GET /equipes` aceita só uma em `unidadeId`. A lista de equipes ativas é curta e
   * já vem com `unidade` no contrato, então o filtro correto sai daqui mesmo — em vez
   * de N requisições, uma por unidade selecionada.
   */
  const equipes = useEquipes()

  const consulta = useQuery({
    queryKey: chaves.usuarios.detalhe(uuid ?? ''),
    queryFn: ({ signal }) => usuariosApi.detalhar(uuid as string, signal),
    enabled: !ehNovo,
    retry: false,
  })

  const alvo = consulta.data

  useEffect(() => {
    if (!alvo) return
    setEstado({
      nome: alvo.nome,
      email: alvo.email,
      papel: alvo.papel,
      unidadeId: alvo.unidadePrincipal ? String(alvo.unidadePrincipal.id) : '',
      unidadeEscopoIds: alvo.unidadesEscopo.map((unidade) => String(unidade.id)),
      equipeIds: alvo.equipes.map((equipe) => String(equipe.id)),
      celular: alvo.celular ?? '',
      receberLeads: alvo.receberLeads,
      ativo: alvo.ativo,
      enviarConvite: false,
    })
  }, [alvo])

  /**
   * Salvar, na edição, é **mais de uma requisição** — e antes desta correção era só
   * uma.
   *
   * `PATCH /usuarios/{uuid}` aceita apenas dado cadastral: papel, equipes, unidades e
   * receber-leads têm rota própria, porque cada uma revoga as sessões do usuário
   * (RN-73) e audita valor anterior e novo (RN-74). A tela carregava esses campos no
   * estado, deixava editar e mandava só o `PATCH` — as alterações de papel, equipe e
   * unidade eram descartadas em silêncio. Era a causa de "gestor não consegue ter duas
   * unidades": não havia caminho na UI para gravar a segunda.
   *
   * **A ordem importa** por causa da invariante "equipe pertence a unidade do
   * usuário", que o servidor confere nos dois sentidos: crescer o conjunto de unidades
   * exige as unidades antes das equipes, encolher exige as equipes antes das unidades.
   * Como uma edição pode fazer as duas coisas (entra "Aluguel", sai "Revenda"), nenhuma
   * ordem única de dois passos serve. São três:
   *
   * 1. equipes reduzidas ao que a configuração **antiga** já sustentava;
   * 2. unidades de escopo e lotação movidas para o valor final;
   * 3. equipes no conjunto final, incluindo as que dependiam da unidade nova.
   *
   * Todo estado intermediário é válido para o servidor, e o resultado final é o da
   * tela. Passos que não mudariam nada são omitidos — cada `PUT` de vínculo revoga as
   * sessões do usuário (RN-73).
   */
  const salvar = useMutation({
    mutationFn: async () => {
      const cadastro = {
        nome: estado.nome.trim(),
        email: estado.email.trim(),
        // `null` explícito limpa o campo na API; `''` no formulário significa "sem
        // celular", não "não mexer".
        celular: montarCelular(estado.celular),
        unidadeId: estado.unidadeId === '' ? null : Number(estado.unidadeId),
      }

      const equipeIds = estado.equipeIds.map(Number)
      const unidadeEscopoIds = estado.unidadeEscopoIds.map(Number)

      if (ehNovo) {
        return usuariosApi.criar({
          ...cadastro,
          papel: estado.papel,
          equipeIds,
          /**
           * `USUARIO_VINCULAR_UNIDADES` não está na matriz do Gestor, e a criação
           * passou a exigi-la como a edição já exigia. Mandar a lista sem ter a
           * permissão renderia 403 e perderia o cadastro inteiro, então quem não pode
           * definir escopo de unidade manda vazio — o Administrador configura depois.
           */
          unidadeEscopoIds: podeVincularUnidades ? unidadeEscopoIds : [],
          receberLeads: estado.receberLeads,
          enviarConvite: estado.enviarConvite,
        })
      }

      if (alvo === undefined) throw new Error('cadastro não carregado')

      const alvoUuid = alvo.uuid
      const unidadesAntes = alvo.unidadesEscopo.map((unidade) => unidade.id)
      const equipesAntes = alvo.equipes.map((equipe) => equipe.id)

      const mexeEmUnidades =
        podeVincularUnidades && !mesmoConjunto(unidadesAntes, unidadeEscopoIds)
      const mexeEmEquipes =
        pode('USUARIO_VINCULAR_EQUIPES') && !mesmoConjunto(equipesAntes, equipeIds)

      /**
       * As equipes finais que **já cabiam** na configuração anterior de unidades.
       *
       * É o estado intermediário que torna qualquer ordem viável: nada aqui depende de
       * unidade nova (então pode ser gravado antes), e nada que bloqueie a remoção de
       * uma unidade sobrevive (porque a seleção final já respeita o escopo novo).
       */
      const permitidasAntes = new Set([
        ...unidadesAntes,
        ...(alvo.unidadePrincipal ? [alvo.unidadePrincipal.id] : []),
      ])
      const equipesQueJaCabiam = equipeIds.filter((id) => {
        const equipe = (equipes.data?.dados ?? []).find((candidata) => candidata.id === id)
        return (
          equipe === undefined || equipe.unidade === null || permitidasAntes.has(equipe.unidade.id)
        )
      })

      let equipesGravadas: ReadonlyArray<number> = equipesAntes

      // 1. Encolhe as equipes para o que a configuração antiga sustenta.
      if ((mexeEmEquipes || mexeEmUnidades) && !mesmoConjunto(equipesQueJaCabiam, equipesGravadas)) {
        await usuariosApi.definirEquipes(alvoUuid, equipesQueJaCabiam)
        equipesGravadas = equipesQueJaCabiam
      }

      // 2. Move as unidades — escopo e lotação. Nenhuma equipe as bloqueia mais.
      if (mexeEmUnidades) {
        await usuariosApi.definirUnidadesEscopo(alvoUuid, unidadeEscopoIds)
      }
      await usuariosApi.atualizar(alvoUuid, cadastro)

      // 3. Agora as equipes que dependiam da unidade nova entram.
      if (mexeEmEquipes && !mesmoConjunto(equipesGravadas, equipeIds)) {
        await usuariosApi.definirEquipes(alvoUuid, equipeIds)
      }

      if (estado.papel !== alvo.papel && pode('USUARIO_ALTERAR_PAPEL')) {
        await usuariosApi.alterarPapel(alvoUuid, estado.papel)
      }

      if (estado.receberLeads !== alvo.receberLeads && pode('USUARIO_ALTERNAR_RECEBER_LEADS')) {
        await usuariosApi.alternarReceberLeads(alvoUuid, estado.receberLeads)
      }

      // Releitura em vez da resposta do último passo: qual foi o último depende do que
      // mudou, e devolver a resposta de um `PATCH` cadastral feito antes da troca de
      // papel deixaria a tela mostrando o estado anterior.
      return usuariosApi.detalhar(alvoUuid)
    },
    onSuccess: (resultado) => {
      setErro(null)
      void clienteConsulta.invalidateQueries({ queryKey: chaves.usuarios.todos })
      avisos.sucesso(
        ehNovo ? 'Usuário criado' : 'Cadastro atualizado',
        ehNovo && estado.enviarConvite
          ? 'Convite de acesso enviado por e-mail com link de definição de senha.'
          : undefined,
      )
      // `void`: `onSuccess` do react-query é síncrono, e a navegação é o efeito
      // final — react-router 7 passou a devolver `Promise` aqui.
      void navegar(`/usuarios/${resultado.uuid}`)
    },
    onError: (causa) => {
      if (causa instanceof ErroApi) setErro(causa)
      else avisos.erro('Não foi possível salvar', causa.message)
    },
  })

  const enviarConvite = useMutation({
    mutationFn: () => usuariosApi.enviarConvite(uuid as string),
    onSuccess: () => avisos.sucesso('Convite enviado', 'O usuário recebeu o link de acesso.'),
    onError: (causa) => avisos.erro('Não foi possível enviar o convite', causa.message),
  })

  if (!ehNovo && consulta.isPending) return <EstadoCarregando />
  if (!ehNovo && consulta.isError) {
    return (
      <EstadoDeErro
        erro={consulta.error}
        aoTentarNovamente={() => void consulta.refetch()}
      />
    )
  }

  /**
   * RN-71: ninguém cria nem promove alguém a papel igual ou superior ao seu.
   *
   * Era filtrado também por `PAPEIS_LIBERADOS_NO_MVP`, que omitia `SUPERVISOR` do
   * seletor. O portão saiu de `@baiocchi/shared` (C18 confirmada) e sobrou a
   * hierarquia, que é a regra de verdade.
   */
  const papeisDisponiveis = PAPEIS.filter((papel) =>
    eu ? podeAtribuirPapel(eu.papel, papel) : false,
  )

  const souEu = alvo?.uuid === eu?.uuid

  /**
   * Unidades que autorizam uma equipe: o escopo **mais** a lotação. É a mesma união
   * que `UsuariosService.conferirEquipesNasUnidades` monta no servidor — a tela
   * espelha a regra para não oferecer opção que a API vai recusar com 422, mas quem
   * decide é o servidor.
   */
  const unidadesPermitidas = new Set<number>(estado.unidadeEscopoIds.map(Number))
  if (estado.unidadeId !== '') unidadesPermitidas.add(Number(estado.unidadeId))

  const equipesDisponiveis = (equipes.data?.dados ?? []).filter(
    (equipe) => equipe.unidade === null || unidadesPermitidas.has(equipe.unidade.id),
  )

  /**
   * Tira da seleção as equipes que deixaram de ser permitidas quando uma unidade sai.
   *
   * Poda explícita, com aviso, em vez de deixar a seleção inválida e receber 422 no
   * salvar: o operador precisa ver que desmarcar "Aluguel" desvincula a equipe
   * "Aluguel A" — e não descobrir isso num erro de campo depois.
   */
  const aplicarUnidades = (
    proximoEscopo: string[],
    proximaLotacao: string = estado.unidadeId,
  ) => {
    const permitidas = new Set<number>(proximoEscopo.map(Number))
    if (proximaLotacao !== '') permitidas.add(Number(proximaLotacao))

    const mantidas = estado.equipeIds.filter((id) => {
      const equipe = (equipes.data?.dados ?? []).find((candidata) => candidata.id === Number(id))
      return equipe === undefined || equipe.unidade === null || permitidas.has(equipe.unidade.id)
    })

    if (mantidas.length !== estado.equipeIds.length) {
      avisos.mostrar({
        tom: 'atencao',
        titulo: 'Equipes desvinculadas',
        descricao:
          'Algumas equipes saíram da seleção porque a unidade delas não está mais vinculada a este usuário.',
      })
    }

    setEstado((atual) => ({
      ...atual,
      unidadeEscopoIds: proximoEscopo,
      unidadeId: proximaLotacao,
      equipeIds: mantidas,
    }))
  }



  return (
    <>
      <CabecalhoPagina
        titulo={ehNovo ? 'Novo usuário' : `Alterar perfil — ${alvo?.nome ?? ''}`}
        descricao={
          ehNovo
            ? 'O acesso é criado com convite: o próprio usuário define a senha pelo link.'
            : 'Alterações de papel, equipe, unidade ou status encerram as sessões deste usuário.'
        }
        acoes={
          <Botao
            variante="contorno"
            onClick={() => navegar('/usuarios')}
            iconeEsquerda={<IconeVoltar className="h-4 w-4" />}
          >
            Voltar
          </Botao>
        }
      />

      <div className="flex flex-col gap-5">
        {erro && !erro.errosPorCampo().length ? <Alerta tom="perigo">{erro.message}</Alerta> : null}

        <div className="grid gap-5 lg:grid-cols-2">
          <Cartao semPadding>
            <CabecalhoCartao titulo="Identificação" />
            <CorpoCartao className="flex flex-col gap-4">
              <Campo rotulo="Nome" obrigatorio erro={erro?.errosPorCampo().nome}>
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={estado.nome}
                    temErro={atributos.temErro}
                    onChange={(evento) =>
                      setEstado((atual) => ({ ...atual, nome: evento.target.value }))
                    }
                  />
                )}
              </Campo>

              <Campo
                rotulo="E-mail"
                obrigatorio
                erro={erro?.errosPorCampo().email}
                dica="Único no sistema. É o login."
              >
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    type="email"
                    value={estado.email}
                    disabled={!ehNovo}
                    temErro={atributos.temErro}
                    onChange={(evento) =>
                      setEstado((atual) => ({ ...atual, email: evento.target.value }))
                    }
                  />
                )}
              </Campo>

              <Campo rotulo="Celular">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    inputMode="tel"
                    value={estado.celular}
                    onChange={(evento) =>
                      setEstado((atual) => ({ ...atual, celular: evento.target.value }))
                    }
                  />
                )}
              </Campo>

              {!ehNovo && alvo ? (
                <div className="border-t border-borda pt-4">
                  <ListaDefinicao
                    colunas={2}
                    itens={[
                      { rotulo: 'Cadastrado em', valor: formatarDataHora(alvo.criadoEm) },
                      { rotulo: 'Último acesso', valor: formatarDataHora(alvo.ultimoAcessoEm) },
                      {
                        rotulo: 'Leads em aberto',
                        valor: (
                          <span className="tabular-nums">{alvo.leadsEmAberto}</span>
                        ),
                      },
                      {
                        rotulo: 'Senha',
                        valor: alvo.deveTrocarSenha ? (
                          <Etiqueta tom="atencao">Troca pendente</Etiqueta>
                        ) : (
                          <Etiqueta tom="sucesso">Definida</Etiqueta>
                        ),
                      },
                    ]}
                  />
                </div>
              ) : null}
            </CorpoCartao>
          </Cartao>

          <Cartao semPadding>
            <CabecalhoCartao titulo="Papel e escopo" />
            <CorpoCartao className="flex flex-col gap-4">
              <Campo
                rotulo="Nível"
                obrigatorio
                erro={erro?.errosPorCampo().papel}
                dica={
                  souEu
                    ? 'Ninguém altera o próprio nível de acesso (RN-70).'
                    : 'Você só pode atribuir papéis abaixo do seu.'
                }
              >
                {(atributos) => (
                  <Selecao
                    {...atributos}
                    value={estado.papel}
                    // RN-70: o servidor recusa com `ACAO_SOBRE_SI_MESMO`. Agora que a
                    // tela realmente envia `PATCH /papel`, desabilitar aqui evita
                    // oferecer uma ação que só pode falhar.
                    disabled={!ehNovo && souEu}
                    temErro={atributos.temErro}
                    opcoes={papeisDisponiveis.map((papel) => ({
                      valor: papel,
                      rotulo: ROTULO_PAPEL[papel],
                    }))}
                    onChange={(evento) =>
                      setEstado((atual) => ({ ...atual, papel: evento.target.value as Papel }))
                    }
                  />
                )}
              </Campo>

              <Campo
                rotulo="Unidade principal"
                obrigatorio
                erro={erro?.errosPorCampo().unidadeId}
                dica="Lotação da pessoa. Não é o que define quais leads ela vê."
              >
                {(atributos) => (
                  <Selecao
                    {...atributos}
                    vazio="Selecione a unidade"
                    value={estado.unidadeId}
                    temErro={atributos.temErro}
                    opcoes={opcoesDeReferencia(unidades.data?.dados)}
                    onChange={(evento) =>
                      aplicarUnidades(estado.unidadeEscopoIds, evento.target.value)
                    }
                  />
                )}
              </Campo>

              {podeVincularUnidades ? (
                <Campo
                  rotulo="Unidades de escopo"
                  erro={erro?.errosPorCampo().unidadeEscopoIds ?? erro?.errosPorCampo().unidadeIds}
                  dica="Um gestor vê os leads de todas as unidades listadas aqui. Pode ser mais de uma."
                >
                  {(atributos) => (
                    <SelecaoMultipla
                      id={atributos.id}
                      buscavel
                      placeholder="Nenhuma unidade de escopo"
                      opcoes={(unidades.data?.dados ?? []).map((unidade) => ({
                        valor: String(unidade.id),
                        rotulo: unidade.titulo,
                      }))}
                      selecionados={estado.unidadeEscopoIds}
                      onAlterar={(valores) => aplicarUnidades(valores)}
                    />
                  )}
                </Campo>
              ) : (
                <Alerta tom="info">
                  As unidades de escopo de um gestor são configuradas pelo Administrador
                  (<code>USUARIO_VINCULAR_UNIDADES</code>). As equipes abaixo são as das unidades
                  já vinculadas a este usuário.
                </Alerta>
              )}

              <Campo
                rotulo="Equipes"
                erro={erro?.errosPorCampo().equipeIds}
                dica={
                  unidadesPermitidas.size === 0
                    ? 'Selecione a unidade principal primeiro.'
                    : 'Um supervisor vê os leads das equipes listadas aqui. Só aparecem equipes das unidades vinculadas ao usuário.'
                }
              >
                {(atributos) => (
                  <SelecaoMultipla
                    id={atributos.id}
                    buscavel
                    desabilitado={unidadesPermitidas.size === 0}
                    placeholder="Nenhuma equipe"
                    opcoes={equipesDisponiveis.map((equipe) => ({
                      valor: String(equipe.id),
                      // A unidade no rótulo porque a lista agora mistura unidades: duas
                      // equipes "Plantão" de unidades diferentes seriam indistinguíveis.
                      rotulo: equipe.unidade ? `${equipe.titulo} · ${equipe.unidade.titulo}` : equipe.titulo,
                    }))}
                    selecionados={estado.equipeIds}
                    onAlterar={(valores) => setEstado((atual) => ({ ...atual, equipeIds: valores }))}
                  />
                )}
              </Campo>

              {estado.papel === 'GESTOR' && estado.unidadeEscopoIds.length === 0 ? (
                <Alerta tom="atencao">
                  Gestor sem unidade de escopo não enxerga lead nenhum — a lista dele vem vazia e
                  parece defeito de permissão. Vincule ao menos uma unidade.
                </Alerta>
              ) : null}

              {estado.papel === 'SUPERVISOR' && estado.equipeIds.length === 0 ? (
                <Alerta tom="atencao">
                  Supervisor sem equipe não enxerga lead nenhum — o escopo dele é a equipe.
                  Vincule ao menos uma.
                </Alerta>
              ) : null}

              <Caixa
                rotulo="Recebe leads"
                dica="Entra na distribuição automática da equipe."
                checked={estado.receberLeads}
                onChange={(evento) =>
                  setEstado((atual) => ({ ...atual, receberLeads: evento.target.checked }))
                }
              />

              {ehNovo ? (
                <>
                  <Caixa
                    rotulo="Ativo"
                    checked={estado.ativo}
                    onChange={(evento) =>
                      setEstado((atual) => ({ ...atual, ativo: evento.target.checked }))
                    }
                  />
                  <Caixa
                    rotulo="Enviar convite de acesso por e-mail"
                    dica="Link de uso único para o próprio usuário definir a senha. Sem o convite, ele fica pendente de ativação."
                    checked={estado.enviarConvite}
                    onChange={(evento) =>
                      setEstado((atual) => ({ ...atual, enviarConvite: evento.target.checked }))
                    }
                  />
                </>
              ) : (
                <div className="flex flex-wrap items-center gap-2 border-t border-borda pt-4">
                  <Etiqueta tom={estado.ativo ? 'sucesso' : 'perigo'}>
                    {estado.ativo ? 'Ativo' : 'Desativado'}
                  </Etiqueta>
                  {estado.ativo ? (
                    <Botao
                      variante="contorno"
                      tamanho="sm"
                      disabled={!pode('USUARIO_ATIVAR_DESATIVAR') || souEu}
                      title={souEu ? 'Você não pode desativar a própria conta.' : undefined}
                      onClick={() => setModalDesativar(true)}
                    >
                      Desativar usuário
                    </Botao>
                  ) : (
                    <Botao
                      variante="contorno"
                      tamanho="sm"
                      disabled={!pode('USUARIO_ATIVAR_DESATIVAR')}
                      onClick={() =>
                        void usuariosApi.ativar(uuid as string).then(() => {
                          void clienteConsulta.invalidateQueries({ queryKey: chaves.usuarios.todos })
                          avisos.sucesso('Usuário reativado')
                        })
                      }
                    >
                      Reativar usuário
                    </Botao>
                  )}
                </div>
              )}
            </CorpoCartao>
          </Cartao>
        </div>

        {!ehNovo && (
          <Cartao semPadding>
            <CabecalhoCartao
              titulo="Carteira de leads"
              descricao="Mover os leads deste usuário em massa (TELA-26)."
            />
            <CorpoCartao className="flex flex-wrap gap-2">
              <Botao
                variante="contorno"
                disabled={!pode('USUARIO_TRANSFERIR_LEADS')}
                onClick={() => setModalTransferir('responsavel')}
                iconeEsquerda={<IconeTransferir className="h-4 w-4" />}
              >
                Transferir leads para outro atendente
              </Botao>
              <Botao
                variante="contorno"
                disabled={!pode('USUARIO_TRANSFERIR_LEADS')}
                onClick={() => setModalTransferir('equipe')}
                iconeEsquerda={<IconeTransferir className="h-4 w-4" />}
              >
                Mudar os leads de equipe
              </Botao>
            </CorpoCartao>
          </Cartao>
        )}

        {!ehNovo && (
          <Cartao semPadding>
            <CabecalhoCartao
              titulo="Acesso"
              descricao="A senha de outra pessoa nunca é definida por aqui."
            />
            <CorpoCartao className="flex flex-col gap-3">
              <Alerta tom="info">
                Para o usuário voltar a acessar, envie um convite: ele recebe um link de uso único e
                define a própria senha. Definir a senha de um colega permitiria assumir a identidade
                dele nos registros de atendimento.
              </Alerta>
              <Botao
                variante="contorno"
                className="self-start"
                disabled={!pode('USUARIO_ENVIAR_CONVITE')}
                carregando={enviarConvite.isPending}
                onClick={() => enviarConvite.mutate()}
              >
                Enviar convite de acesso
              </Botao>
            </CorpoCartao>
          </Cartao>
        )}

        <Cartao>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Botao variante="contorno" onClick={() => navegar('/usuarios')}>
              Cancelar
            </Botao>
            <Botao
              onClick={() => salvar.mutate()}
              carregando={salvar.isPending}
              disabled={!estado.nome.trim() || !estado.email.trim() || !estado.unidadeId}
            >
              {ehNovo ? 'Criar usuário' : 'Atualizar cadastro'}
            </Botao>
          </div>
        </Cartao>
      </div>

      {!ehNovo && (
        <>
          <ModalDesativarUsuario
            aberto={modalDesativar}
            usuarioUuid={uuid as string}
            leadsEmAberto={alvo?.leadsEmAberto ?? 0}
            onFechar={() => setModalDesativar(false)}
            onConcluido={() => {
              setModalDesativar(false)
              void clienteConsulta.invalidateQueries({ queryKey: chaves.usuarios.todos })
              avisos.sucesso('Usuário desativado', 'As sessões dele foram encerradas.')
            }}
          />

          <ModalTransferirCarteira
            modo={modalTransferir}
            usuarioUuid={uuid as string}
            onFechar={() => setModalTransferir(null)}
            onConcluido={(total) => {
              setModalTransferir(null)
              void clienteConsulta.invalidateQueries({ queryKey: chaves.usuarios.todos })
              void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.todos })
              avisos.sucesso(`${total} lead(s) movidos`)
            }}
          />
        </>
      )}
    </>
  )
}

/**
 * Desativação com tratativa dos leads em aberto (FL-14 / RN-07A).
 *
 * O `409 USUARIO_POSSUI_LEADS_EM_ABERTO` é usado como **etapa**: quando ele chega, a
 * tela já está mostrando a escolha do destino, e o reenvio inclui a decisão. Tratar
 * esse código como erro faria a operação achar que a desativação falhou.
 */
function ModalDesativarUsuario({
  aberto,
  usuarioUuid,
  leadsEmAberto,
  onFechar,
  onConcluido,
}: {
  aberto: boolean
  usuarioUuid: string
  leadsEmAberto: number
  onFechar: () => void
  onConcluido: () => void
}) {
  const [tipo, setTipo] = useState<'RESPONSAVEL' | 'EQUIPE' | 'POOL'>('POOL')
  const [responsavelUuid, setResponsavelUuid] = useState('')
  const [equipeId, setEquipeId] = useState('')
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const equipes = useEquipes()
  const membros = useQuery({
    queryKey: chaves.organizacao.membros(Number(equipeId || 0)),
    queryFn: ({ signal }) => equipesApi.membros(Number(equipeId), signal),
    enabled: tipo === 'RESPONSAVEL' && equipeId !== '',
    retry: false,
  })

  const desativar = useMutation({
    mutationFn: () =>
      usuariosApi.desativar(usuarioUuid, {
        motivo: motivo.trim(),
        destino:
          tipo === 'RESPONSAVEL'
            ? { tipo: 'RESPONSAVEL', responsavelUuid }
            : tipo === 'EQUIPE'
              ? { tipo: 'EQUIPE', equipeId: Number(equipeId) }
              : { tipo: 'POOL' },
      }),
    onSuccess: () => {
      setErro(null)
      onConcluido()
    },
    onError: (causa) => {
      if (causa instanceof ErroApi && causa.codigo === CODIGOS_ERRO.ULTIMO_ADMINISTRADOR) {
        setErro(
          'Não é possível desativar o último administrador ativo — o sistema ficaria sem quem gerencia acessos.',
        )
        return
      }
      setErro(causa instanceof Error ? causa.message : 'Não foi possível desativar.')
    },
  })

  const podeEnviar =
    motivo.trim().length >= 5 &&
    (tipo !== 'RESPONSAVEL' || responsavelUuid !== '') &&
    (tipo !== 'EQUIPE' || equipeId !== '')

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      travado={desativar.isPending}
      titulo="Desativar usuário"
      descricao="A desativação encerra todas as sessões e entra na auditoria."
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={desativar.isPending}>
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            onClick={() => desativar.mutate()}
            carregando={desativar.isPending}
            disabled={!podeEnviar}
          >
            Desativar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {erro ? <Alerta tom="perigo">{erro}</Alerta> : null}

        <Alerta tom="atencao" titulo={`${leadsEmAberto} lead(s) em aberto`}>
          Nenhum lead pode ficar órfão. Escolha para onde vão os atendimentos em andamento deste
          usuário antes de desativar.
        </Alerta>

        <Campo rotulo="Destino dos leads em aberto" obrigatorio>
          {(atributos) => (
            <Selecao
              {...atributos}
              value={tipo}
              opcoes={[
                { valor: 'POOL', rotulo: 'Devolver ao pool das equipes (sem responsável)' },
                { valor: 'RESPONSAVEL', rotulo: 'Transferir para outro atendente' },
                { valor: 'EQUIPE', rotulo: 'Mover para outra equipe' },
              ]}
              onChange={(evento) =>
                setTipo(evento.target.value as 'RESPONSAVEL' | 'EQUIPE' | 'POOL')
              }
            />
          )}
        </Campo>

        {(tipo === 'RESPONSAVEL' || tipo === 'EQUIPE') && (
          <Campo rotulo="Equipe" obrigatorio>
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Selecione a equipe"
                value={equipeId}
                opcoes={opcoesDeReferencia(equipes.data?.dados)}
                onChange={(evento) => {
                  setEquipeId(evento.target.value)
                  setResponsavelUuid('')
                }}
              />
            )}
          </Campo>
        )}

        {tipo === 'RESPONSAVEL' && (
          <Campo rotulo="Novo responsável" obrigatorio dica="Somente membros ativos da equipe.">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Selecione o atendente"
                value={responsavelUuid}
                disabled={!equipeId}
                opcoes={opcoesDePessoas(
                  membros.data?.dados.filter((membro) => membro.ativo) ?? [],
                )}
                onChange={(evento) => setResponsavelUuid(evento.target.value)}
              />
            )}
          </Campo>
        )}

        <Campo rotulo="Motivo" obrigatorio dica="Registrado na auditoria.">
          {(atributos) => (
            <Entrada
              {...atributos}
              value={motivo}
              placeholder="Ex.: Desligamento."
              onChange={(evento) => setMotivo(evento.target.value)}
            />
          )}
        </Campo>
      </div>
    </Modal>
  )
}

function ModalTransferirCarteira({
  modo,
  usuarioUuid,
  onFechar,
  onConcluido,
}: {
  modo: 'responsavel' | 'equipe' | null
  usuarioUuid: string
  onFechar: () => void
  onConcluido: (total: number) => void
}) {
  const [equipeId, setEquipeId] = useState('')
  const [responsavelUuid, setResponsavelUuid] = useState('')
  const [apenasEmAberto, setApenasEmAberto] = useState(true)
  const [motivo, setMotivo] = useState('')

  const equipes = useEquipes()
  const membros = useQuery({
    queryKey: chaves.organizacao.membros(Number(equipeId || 0)),
    queryFn: ({ signal }) => equipesApi.membros(Number(equipeId), signal),
    enabled: modo === 'responsavel' && equipeId !== '',
    retry: false,
  })

  const transferir = useMutation({
    mutationFn: () =>
      usuariosApi.transferirLeads(usuarioUuid, {
        motivo: motivo.trim(),
        apenasEmAberto,
        ...(modo === 'equipe'
          ? { equipeId: Number(equipeId) }
          : { responsavelUuid, ...(equipeId ? { equipeId: Number(equipeId) } : {}) }),
      }),
    onSuccess: (resultado) => onConcluido(resultado.transferidos),
  })

  const podeEnviar =
    motivo.trim().length >= 10 &&
    (modo === 'equipe' ? equipeId !== '' : responsavelUuid !== '')

  return (
    <Modal
      aberto={modo !== null}
      onFechar={onFechar}
      travado={transferir.isPending}
      titulo={modo === 'equipe' ? 'Mudar leads de equipe' : 'Transferir leads'}
      descricao="Cada lead movido recebe uma interação de transferência na timeline."
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={transferir.isPending}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => transferir.mutate()}
            carregando={transferir.isPending}
            disabled={!podeEnviar}
          >
            Confirmar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {transferir.isError ? <Alerta tom="perigo">{transferir.error.message}</Alerta> : null}

        <Campo rotulo="Equipe de destino" obrigatorio={modo === 'equipe'}>
          {(atributos) => (
            <Selecao
              {...atributos}
              vazio={modo === 'equipe' ? 'Selecione a equipe' : 'Manter as equipes atuais'}
              value={equipeId}
              opcoes={opcoesDeReferencia(equipes.data?.dados)}
              onChange={(evento) => {
                setEquipeId(evento.target.value)
                setResponsavelUuid('')
              }}
            />
          )}
        </Campo>

        {modo === 'responsavel' && (
          <Campo rotulo="Novo responsável" obrigatorio>
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Selecione o atendente"
                value={responsavelUuid}
                disabled={!equipeId}
                opcoes={opcoesDePessoas(
                  membros.data?.dados.filter((membro) => membro.ativo) ?? [],
                )}
                onChange={(evento) => setResponsavelUuid(evento.target.value)}
              />
            )}
          </Campo>
        )}

        <Caixa
          rotulo="Somente leads em aberto"
          dica="Leads encerrados não são transferíveis."
          checked={apenasEmAberto}
          onChange={(evento) => setApenasEmAberto(evento.target.checked)}
        />

        <Campo
          rotulo="Motivo"
          obrigatorio
          acessorio={<ContadorTexto valor={motivo} minimo={10} maximo={1000} />}
        >
          {(atributos) => (
            <AreaTexto
              {...atributos}
              rows={3}
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
            />
          )}
        </Campo>
      </div>
    </Modal>
  )
}
